import { Nullable } from 'global';
import { DiscountType, QuoteFeeType } from '@trnsact/trnsact-shared-types';
import { CalculatedTotalsByLevel, QuoteChargeListingEntity, QuoteChargeType } from 'modules/quote/types';
import { ChargeDefinition, ChargeType } from '../constants';
import { BasePrice, Charge, EquipmentFormValue } from '../types';

export function calculateTotal(charge: BasePrice): number {
  if (!charge) return 0;

  const { wholesalePrice, markup, markupType, discount, discountType, salesTax, salesTaxType } = charge;

  const calcValue = (value: number, type?: DiscountType) => {
    if (type === DiscountType.Percent) {
      return (value / 100) * wholesalePrice;
    }

    return value;
  };

  const markupAmount = calcValue(markup, markupType);
  const taxAmount = calcValue(salesTax, salesTaxType);
  const discountAmount = calcValue(discount, discountType);

  return wholesalePrice + markupAmount - discountAmount + taxAmount;
}

export function calcDiscount(retailPrice: number, discount: Nullable<number> = 0, type: DiscountType) {
  if (type === DiscountType.Flat) {
    return retailPrice - (discount ?? 0);
  }

  return retailPrice - (retailPrice * (discount ?? 0)) / 100;
}

export const calcAttachmentTotals = (attachments: EquipmentFormValue['attachments']) => {
  return attachments.reduce(
    (acc, { retailPrice, discountValue, discountType, unitCost }) => {
      const customerPriceWithDiscount = calcDiscount(retailPrice, discountValue ?? 0, discountType as any);

      acc.retailPrice += retailPrice;
      acc.wholesaleCost += unitCost;
      acc.customerPriceWithDiscount = customerPriceWithDiscount;

      return acc;
    },
    {
      retailPrice: 0,
      wholesaleCost: 0,
      customerPriceWithDiscount: 0,
    }
  );
};

export const addValueFieldToCharges = (charges: Record<ChargeType, ChargeDefinition[]>) => {
  return Object.entries(charges).reduce(
    (acc, [key, chargeArray]) => {
      acc[key as ChargeType] = chargeArray.map(charge => ({
        ...charge,
        value: false,
      }));

      return acc;
    },
    {} as Record<ChargeType, (ChargeDefinition & { value: boolean })[]>
  );
};

export function calcFees(charge: Nullable<Charge>) {
  const fees = [QuoteFeeType.DocFee, QuoteFeeType.TireFee, QuoteFeeType.TitleFee, QuoteFeeType.Other];

  if (!charge || !fees.includes(charge.type)) return 0;

  return calculateTotal(charge);
}

export function calcTaxes(charge: Nullable<Charge>) {
  const taxes = [
    QuoteFeeType.CityTax,
    QuoteFeeType.CountyTax,
    QuoteFeeType.StateTax,
    QuoteFeeType.FederalExciseTax,
    QuoteFeeType.VehicleInventoryTax,
  ];

  if (!charge || !taxes.includes(charge.type)) return 0;

  return calculateTotal(charge);
}

export function getCalculatedTotalsByLevel(bodyFields: QuoteChargeListingEntity[]): CalculatedTotalsByLevel {
  const totals: CalculatedTotalsByLevel = {
    invoice: { fees: 0, taxes: 0, price: 0 },
    equipments: { fees: 0, taxes: 0, price: 0 },
    attachments: { fees: 0, taxes: 0, price: 0 },
  };

  for (const row of bodyFields) {
    if (row.type === QuoteChargeType.Invoice) {
      totals.invoice.fees += calcFees(row.charge);
      totals.invoice.taxes += calcTaxes(row.charge);
      totals.invoice.price += calculateTotal(row.charge!);
    }

    if (row.type === QuoteChargeType.Equipment && row.equipment) {
      const { price } = row.equipment;

      totals.equipments.price += calculateTotal(price);

      for (const att of row.equipment.attachments ?? []) {
        totals.attachments.price += calculateTotal(att.price);
      }
    }
  }

  return totals;
}
